/** Page (relative to the add-in root) rendered inside the Deal Tracker dialog. */
export const DEAL_TRACKER_DIALOG_PAGE = "deal-tracker-dialog.html";

/** Message the dialog page sends via messageParent to ask the task pane to close it. */
export const DEAL_TRACKER_DIALOG_CLOSE_MESSAGE = "triyo-deal-tracker-dialog-close";

/** displayDialogAsync error code raised when the add-in already has a dialog open. */
const DIALOG_ALREADY_OPEN_CODE = 12007;

/**
 * Dialog size in percent of the Office host window. Wider than the other add-in
 * dialogs on purpose: the Deal Tracker is a 13-column grid, and the two frozen
 * columns only leave useful room for the rest at this width.
 */
const DIALOG_SIZE = { height: 75, width: 60 } as const;

export type DealTrackerDisplay = "pane" | "dialog";

/**
 * Resolves the configured Deal Tracker surface (`dealTrackerDisplay` in
 * public/config.js). Defaults to "dialog" — the grid is wider than a task pane.
 */
export function getDealTrackerDisplay(): DealTrackerDisplay {
  return window.APP_SETTINGS?.dealTrackerDisplay === "pane" ? "pane" : "dialog";
}

let activeDialog: Office.Dialog | null = null;

function closeActiveDialog(): void {
  activeDialog?.close();
  activeDialog = null;
}

/**
 * Opens the Deal Tracker page in an Office dialog over the Outlook window, or a
 * popup outside Office (browser preview). Resolves "opened" while a dialog is
 * showing — including one already open — and "unavailable" when nothing could be
 * opened, in which case callers fall back to the inline task-pane view.
 */
export function openDealTrackerDialog(): Promise<"opened" | "unavailable"> {
  const url = new URL(DEAL_TRACKER_DIALOG_PAGE, window.location.href).toString();

  if (typeof Office !== "undefined" && Office.context?.ui?.displayDialogAsync) {
    return new Promise((resolve) => {
      Office.context.ui.displayDialogAsync(
        url,
        { height: DIALOG_SIZE.height, width: DIALOG_SIZE.width, displayInIframe: true },
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            // 12007 fires when the add-in has ANY dialog open (e.g. the SSO
            // web-login dialog); only ours counts as "opened".
            const dealTrackerDialogAlreadyOpen =
              result.error?.code === DIALOG_ALREADY_OPEN_CODE && activeDialog !== null;
            resolve(dealTrackerDialogAlreadyOpen ? "opened" : "unavailable");
            return;
          }
          const dialog = result.value;
          activeDialog = dialog;

          // Both handlers check they still own the module handle. A close event
          // for a previous dialog can arrive after the next one has opened, and
          // without the guard it would clear the live dialog's handle -- leaving
          // that dialog unclosable and later opens misread as "unavailable".
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: { message: string; origin: string | undefined } | { error: number }) => {
              if (activeDialog !== dialog) return;
              if ("message" in arg && arg.message === DEAL_TRACKER_DIALOG_CLOSE_MESSAGE) {
                closeActiveDialog();
              }
            }
          );
          dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
            if (activeDialog === dialog) activeDialog = null;
          });
          resolve("opened");
        }
      );
    });
  }

  // No "noopener": the page is our own same-origin entry, and with it in the
  // features string window.open returns null even when the popup opens.
  const popup = window.open(url, "_blank", "popup=yes,width=900,height=760");
  return Promise.resolve(popup ? "opened" : "unavailable");
}

/**
 * Called from inside the dialog page: asks the hosting task pane to close this
 * dialog, falling back to window.close() outside Office (browser preview).
 */
export function requestDealTrackerDialogClose(): void {
  if (typeof Office !== "undefined" && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(DEAL_TRACKER_DIALOG_CLOSE_MESSAGE);
    return;
  }
  window.close();
}
